/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbKeySet } from "abweb/collection/AbKeySet";
import { useMemo } from "react";
export let AbSelectionPropsUtils;
(function (_AbSelectionPropsUtils) {
  const removeSelectionProps = _AbSelectionPropsUtils.removeSelectionProps = props => {
    if (props.selectionMode === SelectionMode.SINGLE || props.selectionMode === SelectionMode.MULTIPLE) {
      const {
        selectionMode,
        onChange,
        value,
        ...rest
      } = props;
      return rest;
    } else {
      const {
        selectionMode,
        ...rest
      } = props;
      return rest;
    }
  };
  const mapSingleOrMultiSelectionProps = _AbSelectionPropsUtils.mapSingleOrMultiSelectionProps = (props, m1, m2) => {
    const propsKeyFunction = props.keyFunction;
    const newKeyFunction = propsKeyFunction ? item => propsKeyFunction(m2(item)) : undefined;
    switch (props.selectionMode) {
      case SelectionMode.SINGLE:
        const {
          item: rawItem,
          enrichment
        } = AbEnriched.extract(props.value);
        return {
          selectionMode: SelectionMode.SINGLE,
          onChange: (item, maybeEnrichedItem) => {
            props.onChange(m2(item), AbEnriched.createMaybe(m2(item), AbEnriched.getEnrichment(maybeEnrichedItem)));
          },
          value: rawItem === undefined ? undefined : AbEnriched.createMaybe(m1(rawItem), enrichment),
          keyFunction: newKeyFunction
        };
      case SelectionMode.MULTIPLE:
        const convertedValues = Array.from(props.value).map(maybeEnrichedItem => AbEnriched.createMaybe(m1(AbEnriched.getItem(maybeEnrichedItem)), AbEnriched.getEnrichment(maybeEnrichedItem)));
        const convertedValuesSet = AbKeySet.from(convertedValues, AbEnriched.createMaybeEnrichedKeyFunction(newKeyFunction));
        return {
          selectionMode: SelectionMode.MULTIPLE,
          onChange: (set, maybeEnrichedSet) => {
            const convertedMaybeEnrichedSet = maybeEnrichedSet.map(maybeEnrichedItem => AbEnriched.createMaybe(m2(AbEnriched.getItem(maybeEnrichedItem)), AbEnriched.getEnrichment(maybeEnrichedItem)));
            props.onChange(set.map(m2), convertedMaybeEnrichedSet);
          },
          value: convertedValuesSet.toSet(),
          keyFunction: newKeyFunction
        };
    }
  };
  const mapSelectionProps = _AbSelectionPropsUtils.mapSelectionProps = (props, m1, m2) => {
    switch (props.selectionMode) {
      case SelectionMode.NONE:
      case undefined:
        const propsKeyFunction = props.keyFunction;
        return {
          selectionMode: SelectionMode.NONE,
          keyFunction: propsKeyFunction ? item => propsKeyFunction(m2(item)) : undefined
        };
      case SelectionMode.SINGLE:
      case SelectionMode.MULTIPLE:
        return mapSingleOrMultiSelectionProps(props, m1, m2);
    }
  };
  const selectionPropsSelect = _AbSelectionPropsUtils.selectionPropsSelect = (props, item, maybeEnrichedItem) => {
    switch (props.selectionMode) {
      case SelectionMode.SINGLE:
        props.onChange(item, maybeEnrichedItem);
        break;
      case SelectionMode.MULTIPLE:
        const valueSet = AbKeySet.from(Array.from(props.value, AbEnriched.getItem), props.keyFunction);
        const maybeEnhancedValueSet = AbKeySet.from(props.value, AbEnriched.createMaybeEnrichedKeyFunction(props.keyFunction));
        const onChangeSet = valueSet.has(item) ? valueSet.difference([item]) : valueSet.union([item]);
        const onChangeMaybeEnrichedSet = maybeEnhancedValueSet.has(maybeEnrichedItem) ? maybeEnhancedValueSet.difference([maybeEnrichedItem]) : maybeEnhancedValueSet.union([maybeEnrichedItem]);
        props.onChange(onChangeSet.toSet(), onChangeMaybeEnrichedSet.toSet());
        break;
    }
  };
})(AbSelectionPropsUtils || (AbSelectionPropsUtils = {}));
/**
 * Extracts the selectionProps from anything that extends AbSelectionModeProps
 */
export const getSelectionProps = props => {
  if (props.selectionMode === SelectionMode.SINGLE || props.selectionMode === SelectionMode.MULTIPLE) {
    const {
      selectionMode,
      onChange,
      value
    } = props;
    return {
      selectionMode,
      onChange,
      value
    };
  } else {
    const {
      selectionMode
    } = props;
    return {
      selectionMode
    };
  }
};

/**
 * Returns true if the potentialSelection is selected by the selectionProps
 */
export function isValueSelected(potentialSelection, selectionProps) {
  switch (selectionProps.selectionMode) {
    case SelectionMode.MULTIPLE:
      return selectionProps.value.has(potentialSelection);
    case SelectionMode.SINGLE:
      return selectionProps.value === potentialSelection;
    default:
      return false;
  }
}

/**
 * gets selection props but keeps them reference stable until they actually change
 */
export function useStableSelectionProps(props) {
  const selectionProps = getSelectionProps(props);
  const selectionMode = selectionProps.selectionMode;
  let onChange = undefined;
  if ("onChange" in selectionProps) {
    onChange = selectionProps.onChange;
  }
  let value = undefined;
  if ("value" in selectionProps) {
    value = selectionProps.value;
  }
  return useMemo(() => {
    return selectionProps;
  },
  // eslint-disable-next-line  abinitio/exhaustive-deps
  [selectionMode, onChange, value]);
}
//# sourceMappingURL=AbSelectionPropsUtils.js.map
